import React, { useState, useEffect, useRef } from 'react';
import {
  CurrencyDollarIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  ArchiveBoxIcon,
  BuildingOfficeIcon,
  CogIcon,
  ChartBarIcon,
  BriefcaseIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  ComputerDesktopIcon,
  CheckCircleIcon,
  PlusIcon,
  SparklesIcon,
  StarIcon,
  TagIcon,
  BanknotesIcon,
  AcademicCapIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon as SparklesIconSolid } from '@heroicons/react/24/solid';

import axiosInstance from '../api/axiosInstance';
import Button from '../ui/Button';
import { SearchInput } from '../ui/Input';
import Card from '../ui/Card';
import { StatusBadge } from '../ui/Table';
import logger from '../../utils/logger';

const ERPModuleCatalog = ({ showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [ownedModules, setOwnedModules] = useState([]);
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [togglingModule, setTogglingModule] = useState(null); // track which module is being toggled
  const scrollContainerRef = useRef(null);

  // Available ERP modules
  const availableModules = [
    {
      id: 'financial',
      name: 'Financial',
      description: 'Accounting, invoicing, payments, and financial reporting',
      icon: CurrencyDollarIcon,
      gradient: 'from-emerald-500 to-green-600',
      category: 'finance',
      popular: true,
      features: ['General Ledger', 'Accounts Payable/Receivable', 'Financial Reporting', 'Budgeting', 'Tax Management']
    },
    {
      id: 'sales',
      name: 'Sales',
      description: 'Sales pipeline, lead scoring, and conversion analytics',
      icon: ShoppingCartIcon,
      gradient: 'from-blue-500 to-blue-600',
      category: 'sales',
      popular: true,
      features: ['Lead Management', 'Opportunity Tracking', 'Quote Generation', 'Sales Analytics', 'Commission Tracking']
    },
    {
      id: 'crm',
      name: 'CRM',
      description: 'Customer management, interaction history, and campaigns',
      icon: UserGroupIcon,
      gradient: 'from-purple-500 to-violet-600',
      category: 'sales',
      popular: true,
      features: ['Contact Management', 'Interaction History', 'Marketing Automation', 'Customer Analytics', 'Support Ticketing']
    },
    {
      id: 'inventory',
      name: 'Inventory',
      description: 'Stock control, warehousing, and automated reordering',
      icon: ArchiveBoxIcon,
      gradient: 'from-orange-500 to-amber-600',
      category: 'operations',
      popular: false,
      features: ['Stock Management', 'Warehouse Operations', 'Demand Forecasting', 'Vendor Management', 'Barcode Scanning']
    },
    {
      id: 'hr',
      name: 'HR',
      description: 'Employee management, payroll, and attendance',
      icon: BriefcaseIcon,
      gradient: 'from-pink-500 to-rose-600',
      category: 'hr',
      popular: false,
      features: ['Employee Records', 'Payroll Processing', 'Benefits Management', 'Performance Reviews', 'Time Tracking']
    },
    {
      id: 'procurement',
      name: 'Procurement',
      description: 'Purchase orders, vendor management, and cost optimization',
      icon: ClipboardDocumentListIcon,
      gradient: 'from-yellow-500 to-orange-600',
      category: 'operations',
      popular: false,
      features: ['Purchase Orders', 'Vendor Management', 'Contract Management', 'Spend Analytics', 'Approval Workflows']
    },
    {
      id: 'manufacturing',
      name: 'Manufacturing',
      description: 'Production scheduling, work orders, and quality control',
      icon: WrenchScrewdriverIcon,
      gradient: 'from-gray-500 to-slate-600',
      category: 'operations',
      popular: false,
      features: ['Production Planning', 'Work Orders', 'Quality Control', 'Equipment Maintenance', 'Shop Floor Control']
    },
    {
      id: 'projects',
      name: 'Projects',
      description: 'Project management with Kanban boards and collaboration',
      icon: BuildingOfficeIcon,
      gradient: 'from-teal-500 to-cyan-600',
      category: 'operations',
      popular: true,
      features: ['Project Planning', 'Task Management', 'Resource Allocation', 'Time Tracking', 'Collaboration Tools']
    },
    {
      id: 'pos',
      name: 'Point of Sale',
      description: 'POS system with offline capability and integrated payments',
      icon: ComputerDesktopIcon,
      gradient: 'from-indigo-500 to-blue-600',
      category: 'sales',
      popular: false,
      features: ['Sales Transactions', 'Cash Drawer Management', 'Receipt Printing', 'Shift Management', 'Offline Support']
    },
    {
      id: 'reports',
      name: 'Reports',
      description: 'Custom dashboards, analytics, and AI-powered insights',
      icon: ChartBarIcon,
      gradient: 'from-red-500 to-pink-600',
      category: 'analytics',
      popular: false,
      features: ['Report Builder', 'Dashboard Builder', 'Scheduled Reports', 'Data Export', 'Custom Analytics']
    },
    {
      id: 'restaurant',
      name: 'Restaurant',
      description: 'Table management, menu, kitchen display, and order tracking',
      icon: ShoppingBagIcon,
      gradient: 'from-amber-500 to-orange-600',
      category: 'hospitality',
      popular: false,
      features: ['Table Management', 'Menu Builder', 'Kitchen Display', 'Order Tracking', 'Reservation System']
    },
    {
      id: 'sms',
      name: 'School Management',
      description: 'Student records, attendance, grading, and fee management',
      icon: AcademicCapIcon,
      gradient: 'from-sky-500 to-blue-600',
      category: 'education',
      popular: false,
      features: ['Student Enrollment', 'Attendance Tracking', 'Grade Management', 'Parent Portal', 'Fee Management']
    }
  ];

  useEffect(() => {
    fetchModuleData();
  }, []);

  const fetchModuleData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/business/modules/license').catch((error) => {
        if (error.response?.status === 404) {
          return { data: { success: false, data: { licensed: [], tier: 'starter' } } };
        }
        throw error;
      });

      if (response.data.success) {
        const { licensed, tier } = response.data.data;
        const ownedModulesFormatted = licensed.map(moduleId => ({
          moduleId,
          status: 'active',
          tier: tier
        }));
        setOwnedModules(ownedModulesFormatted);
        setIsEnterprise(tier === 'enterprise');
      } else {
        setOwnedModules([]);
        setIsEnterprise(false);
      }
    } catch (error) {
      logger.error('Error fetching module data:', error);
      if (error.response && error.response.status !== 404) {
        showNotification?.('Error loading module information', 'error');
      }
      setOwnedModules([]);
      setIsEnterprise(false);
    } finally {
      setLoading(false);
    }
  };

  // Silently refresh module data without resetting loading state or scroll
  const refreshModuleData = async () => {
    try {
      const response = await axiosInstance.get('/api/business/modules/license').catch((error) => {
        if (error.response?.status === 404) {
          return { data: { success: false, data: { licensed: [], tier: 'starter' } } };
        }
        throw error;
      });

      if (response.data.success) {
        const { licensed, tier } = response.data.data;
        const ownedModulesFormatted = licensed.map(moduleId => ({
          moduleId,
          status: 'active',
          tier: tier
        }));
        setOwnedModules(ownedModulesFormatted);
        setIsEnterprise(tier === 'enterprise');
      } else {
        setOwnedModules([]);
        setIsEnterprise(false);
      }
    } catch (error) {
      logger.error('Error refreshing module data:', error);
    }
  };

  const filteredModules = availableModules.filter(module => {
    const matchesSearch = module.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         module.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' ||
                           (categoryFilter === 'popular' && module.popular) ||
                           (categoryFilter === 'owned' && ownedModules.some(owned => owned.moduleId === module.id)) ||
                           module.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleToggleModule = async (module) => {
    const isOwned = ownedModules.some(owned => owned.moduleId === module.id);
    setTogglingModule(module.id);

    try {
      if (isOwned) {
        const response = await axiosInstance.post('/api/business/modules/deactivate', {
          modules: [module.id]
        });
        if (response.data.success) {
          showNotification?.(`${module.name} deactivated`, 'success');
          await refreshModuleData();
        } else {
          showNotification?.(response.data.message || 'Failed to deactivate module', 'error');
        }
      } else {
        const response = await axiosInstance.post('/api/business/modules/activate', {
          modules: [module.id],
          tier: 'professional'
        });
        if (response.data.success) {
          showNotification?.(`${module.name} activated`, 'success');
          await refreshModuleData();
        } else {
          showNotification?.(response.data.message || 'Failed to activate module', 'error');
        }
      }
    } catch (error) {
      logger.error('Error toggling module:', error);
      showNotification?.(
        error.response?.data?.message || 'Error updating module',
        'error'
      );
    } finally {
      setTogglingModule(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-[#111111] flex flex-col">
      {/* Compact Header */}
      <div className="border-b border-gray-200 dark:border-gray-800 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CogIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">ERP Module Catalog</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {ownedModules.length} of {availableModules.length} modules activated
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="border-b border-gray-200 dark:border-gray-800 px-6 py-2.5">
        <div className="flex items-center space-x-4">
          <div className="flex-1 max-w-md">
            <SearchInput
              placeholder="Search modules..."
              value={searchQuery}
              onSearch={setSearchQuery}
              size="sm"
            />
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white rounded focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Categories</option>
            <option value="popular">Popular</option>
            <option value="owned">Activated</option>
            <option value="finance">Finance</option>
            <option value="sales">Sales</option>
            <option value="operations">Operations</option>
            <option value="hr">Human Resources</option>
            <option value="analytics">Analytics</option>
          </select>
        </div>
      </div>

      {/* Module Grid */}
      <div className="flex-1 p-4 overflow-y-auto" ref={scrollContainerRef}>
        {filteredModules.length === 0 ? (
          <div className="text-center py-12">
            <CogIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No modules found</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Try adjusting your search or filter.</p>
            <button
              onClick={() => { setSearchQuery(''); setCategoryFilter('all'); }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filteredModules.map((module) => {
              const isOwned = ownedModules.some(owned => owned.moduleId === module.id);
              const isToggling = togglingModule === module.id;
              const Icon = module.icon;

              return (
                <div
                  key={module.id}
                  className={`relative flex flex-col bg-white dark:bg-[#1a1a1a] rounded-lg border transition-all ${
                    isOwned
                      ? 'border-green-300 dark:border-green-700 shadow-sm'
                      : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                  }`}
                >
                  {/* Card Body */}
                  <div className="p-3 flex flex-col flex-1">
                    {/* Icon + Name row */}
                    <div className="flex items-center space-x-2.5 mb-2">
                      <div className={`w-8 h-8 shrink-0 bg-gradient-to-br ${module.gradient} rounded-lg flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight">
                          {module.name}
                        </h3>
                        {/* Badges inline with name */}
                        <div className="flex items-center gap-1 mt-0.5">
                          {module.popular && (
                            <span className="inline-flex items-center px-1 py-px rounded text-[10px] font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                              Popular
                            </span>
                          )}
                          {isOwned && (
                            <span className="inline-flex items-center px-1 py-px rounded text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                              Active
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-2 mb-3">
                      {module.description}
                    </p>

                    {/* Features - fixed height: always show exactly 3 */}
                    <div className="space-y-1 mt-auto">
                      {module.features.slice(0, 3).map((feature, index) => (
                        <div key={index} className="flex items-center space-x-1.5">
                          <div className="w-1 h-1 shrink-0 bg-blue-500 rounded-full"></div>
                          <span className="text-[11px] text-gray-600 dark:text-gray-400 truncate">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Toggle Button */}
                  <div className="px-3 pb-3 pt-1">
                    <button
                      onClick={() => handleToggleModule(module)}
                      disabled={isToggling}
                      className={`w-full px-2 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                        isOwned
                          ? 'bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isToggling ? (
                        <span className="flex items-center justify-center space-x-1">
                          <span className="animate-spin rounded-full h-3 w-3 border border-current border-t-transparent"></span>
                          <span>Updating...</span>
                        </span>
                      ) : isOwned ? (
                        'Deactivate'
                      ) : (
                        'Activate'
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ERPModuleCatalog;
